import { MapPin, Navigation } from 'lucide-react';
import { Badge } from './ui/badge';

const ServiceArea = () => {
  // Center coordinates for Babusapalya, Bangalore 560043
  const centerLat = 13.0452;
  const centerLng = 77.6368;
  
  // Google Maps embed URL centered on Babusapalya with zoom level showing ~10km radius
  const mapEmbedUrl = `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d62192.94762694774!2d77.5868!3d13.0452!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae17600e8b3947%3A0x8add71c4a63a19b8!2sBabusapalya%2C%20Bengaluru%2C%20Karnataka%20560043!5e0!3m2!1sen!2sin!4v1704067200000!5m2!1sen!2sin`;

  const coverageHighlights = [
    { direction: 'North', areas: 'Hebbal, Yelahanka, Jakkur, Airport', keywords: 'snake catcher hebbal' },
    { direction: 'South', areas: 'Bangalore Central, Shivajinagar', keywords: 'snake rescue central bangalore' },
    { direction: 'East', areas: 'KR Puram, Ramamurthy Nagar, Banaswadi', keywords: 'snake catcher ramamurthy nagar, snake rescue banaswadi' },
    { direction: 'West', areas: 'RT Nagar, Sanjaynagar, Sahakarnagar', keywords: 'snake catcher rt nagar' },
  ];

  return (
    <section 
      id="service-area"
      className="py-20 md:py-32 bg-[#F5F3ED]"
      data-testid="service-area-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section Header */}
        <div className="text-center mb-12">
          <Badge variant="outline" className="mb-4 text-[#2A4B35] border-[#2A4B35]">
            <MapPin className="w-3 h-3 mr-1" />
            Snake Catcher Near Me
          </Badge>
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1D1A] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="service-area-heading"
          >
            Snake Rescue Near Me - Areas We Cover
          </h2>
          <p className="text-[#4A4D4A] text-lg max-w-2xl mx-auto">
            Looking for <strong>snake catcher near me</strong>? We serve a <span className="font-semibold text-[#2A4B35]">10km radius</span> from Babusapalya, Bangalore 560043
          </p>
        </div>

        {/* Map and Coverage Info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Map Embed */}
          <div className="lg:col-span-2 rounded-2xl overflow-hidden shadow-lg border border-[#D8D4C7]">
            <iframe
              src={mapEmbedUrl}
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Snake Rescue Service Area - Bangalore"
              className="w-full"
              data-testid="service-area-map"
            ></iframe>
          </div>

          {/* Coverage Details */}
          <div className="space-y-6">
            {/* Radius Info */}
            <div className="bg-[#2A4B35] rounded-2xl p-6 text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-[#25D366]/20 rounded-xl flex items-center justify-center">
                  <Navigation className="w-6 h-6 text-[#25D366]" />
                </div>
                <div>
                  <h3 className="font-bold text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    10km Radius
                  </h3>
                  <p className="text-[#C5D4C9] text-sm">from Babusapalya</p>
                </div>
              </div>
              <p className="text-[#C5D4C9] text-sm leading-relaxed">
                We provide 24/7 emergency snake rescue services within a 10km radius of our base location at Babusapalya, Bangalore 560043.
              </p>
            </div>

            {/* Direction-wise Coverage */}
            <div className="bg-white rounded-2xl p-6 border border-[#D8D4C7]">
              <h4 className="font-bold text-[#1A1D1A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Coverage by Direction
              </h4>
              <div className="space-y-3">
                {coverageHighlights.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <span className="bg-[#2A4B35] text-white text-xs font-bold px-2 py-1 rounded min-w-[50px] text-center">
                      {item.direction}
                    </span>
                    <span className="text-[#4A4D4A] text-sm">{item.areas}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Response */}
            <div className="bg-[#EAE7DF] rounded-2xl p-6">
              <h4 className="font-bold text-[#1A1D1A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Quick Response Time
              </h4>
              <p className="text-[#4A4D4A] text-sm mb-3">
                Within our service area, we typically arrive in:
              </p>
              <div className="flex items-center gap-2">
                <span className="bg-[#25D366] text-white font-bold px-3 py-1 rounded-full text-sm">
                  15-30 mins
                </span>
                <span className="text-[#4A4D4A] text-sm">depending on traffic</span>
              </div>
            </div>
          </div>
        </div>

        {/* Areas List */}
        <div className="mt-12 bg-white rounded-2xl p-8 border border-[#D8D4C7]">
          <h3 className="font-bold text-[#1A1D1A] mb-6 text-center text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Snake Catcher Available In These Localities
          </h3>
          <div className="flex flex-wrap justify-center gap-2">
            {[
              'Babusapalya', 'KR Puram', 'Hebbal', 'Yelahanka', 'Kempegowda Airport',
              'Bangalore Central', 'Kalyan Nagar', 'Hennur', 'Banaswadi', 'Ramamurthy Nagar',
              'Kasturi Nagar', 'HRBR Layout', 'Kammanahalli', 'Lingarajapuram', 'RT Nagar',
              'Sanjaynagar', 'Nagawara', 'Thanisandra', 'Sahakarnagar', 'Jakkur',
              'Horamavu', 'Vidyaranyapura', 'Kogilu', 'Dasarahalli', 'Bagalur'
            ].map((area, index) => (
              <Badge 
                key={index}
                variant="secondary"
                className="bg-[#EAE7DF] text-[#2A4B35] border-transparent hover:bg-[#2A4B35] hover:text-white transition-colors cursor-default"
                title={`Snake catcher ${area}, Snake rescue ${area}`}
              >
                {area}
              </Badge>
            ))}
          </div>
          <p className="text-center text-[#4A4D4A] text-sm mt-6">
            Don't see your area? <a href="tel:+918310755854" className="text-[#2A4B35] font-semibold hover:underline">Call snake rescue number</a> to check availability
          </p>
        </div>
      </div>
    </section>
  );
};

export default ServiceArea;
